import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  Divider,
  Stack,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Save as SaveIcon,
  Business as BusinessIcon,
  AddBusiness as AddBusinessIcon,
} from '@mui/icons-material';
import merchantService from '../services/merchant.service';
import LoadingAnimation from '../components/LoadingAnimation';

// Categories accepted by the Business model.
const BUSINESS_CATEGORIES = ['Bakery', 'Cafe', 'Restaurant', 'Desserts', 'Breakfast', 'Meals'];

// Approval status → banner/chip presentation. New businesses are 'pending'
// until an admin verifies them.
const STATUS_INFO: Record<string, { label: string; color: 'warning' | 'success' | 'error' | 'default'; severity: 'info' | 'success' | 'warning' | 'error'; message: string }> = {
  pending: {
    label: 'Pending Approval',
    color: 'warning',
    severity: 'warning',
    message: 'Your business is awaiting admin approval. It will be listed to customers once verified.',
  },
  verified: {
    label: 'Verified',
    color: 'success',
    severity: 'success',
    message: 'Your business is verified and listed to customers.',
  },
  rejected: {
    label: 'Rejected',
    color: 'error',
    severity: 'error',
    message: 'Your business registration was rejected. Please contact support for details.',
  },
  suspended: {
    label: 'Suspended',
    color: 'error',
    severity: 'error',
    message: 'Your business has been suspended and is not visible to customers. Please contact support.',
  },
};

export default function MerchantBusiness() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [business, setBusiness] = useState<any>(null);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [createForm, setCreateForm] = useState({
    name: '',
    category: 'Bakery',
    description: '',
    address: '',
    city: '',
    latitude: '',
    longitude: '',
    phone: '',
    email: '',
  });
  const [formData, setFormData] = useState({
    description: '',
    address: '',
    city: '',
    latitude: '',
    longitude: '',
    phone: '',
    email: '',
    businessHours: {
      monday: { open: '09:00', close: '18:00', closed: false },
      tuesday: { open: '09:00', close: '18:00', closed: false },
      wednesday: { open: '09:00', close: '18:00', closed: false },
      thursday: { open: '09:00', close: '18:00', closed: false },
      friday: { open: '09:00', close: '18:00', closed: false },
      saturday: { open: '09:00', close: '18:00', closed: false },
      sunday: { open: '09:00', close: '18:00', closed: true },
    },
  });

  useEffect(() => {
    fetchBusiness();
  }, []);

  const fetchBusiness = async () => {
    try {
      const businessData = await merchantService.getBusiness();
      setBusiness(businessData);
      setFormData({
        description: businessData.description || '',
        address: businessData.location?.address || '',
        city: businessData.location?.city || '',
        latitude: businessData.location?.coordinates?.latitude?.toString() || '',
        longitude: businessData.location?.coordinates?.longitude?.toString() || '',
        phone: businessData.contact?.phone || '',
        email: businessData.contact?.email || '',
        businessHours: businessData.businessHours || formData.businessHours,
      });
      setLoading(false);
    } catch (error) {
      console.error('Error fetching business:', error);
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      setCreating(true);
      setCreateError('');

      const required: Array<[string, string]> = [
        [createForm.name, 'business name'],
        [createForm.description, 'description'],
        [createForm.address, 'address'],
        [createForm.city, 'city'],
        [createForm.phone, 'phone number'],
        [createForm.email, 'email'],
      ];
      const missing = required.filter(([value]) => !value.trim()).map(([, label]) => label);
      if (missing.length) {
        setCreateError(`Please provide: ${missing.join(', ')}`);
        setCreating(false);
        return;
      }

      await merchantService.createBusiness({
        name: createForm.name,
        description: createForm.description,
        category: createForm.category,
        location: {
          address: createForm.address,
          city: createForm.city,
          coordinates: {
            latitude: parseFloat(createForm.latitude) || 0,
            longitude: parseFloat(createForm.longitude) || 0,
          },
        },
        contact: {
          phone: createForm.phone,
          email: createForm.email,
        },
      });

      // Reload so the page switches to the edit view with the pending banner.
      setLoading(true);
      await fetchBusiness();
    } catch (error: any) {
      console.error('Error creating business:', error);
      // The backend's central error handler returns { error: { message, status } },
      // while route-level validation returns { error: '<string>' }. Always resolve
      // to a string so we never render an object as a React child (which crashes
      // the page with "Objects are not valid as a React child").
      const data = error.response?.data;
      const errMsg =
        (typeof data?.error === 'string' ? data.error : data?.error?.message) ||
        data?.message ||
        error.message ||
        'Failed to register business';
      setCreateError(errMsg);
    } finally {
      setCreating(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setErrorMessage('');
      setSaveMessage('');

      // Validate coordinates
      const lat = parseFloat(formData.latitude);
      const lng = parseFloat(formData.longitude);

      if (isNaN(lat) || isNaN(lng)) {
        setErrorMessage('Please enter valid latitude and longitude values');
        setSaving(false);
        return;
      }

      const result = await merchantService.updateBusiness({
        description: formData.description,
        location: {
          address: formData.address,
          city: formData.city,
          coordinates: {
            latitude: lat,
            longitude: lng,
          },
        },
        contact: {
          phone: formData.phone,
          email: formData.email,
        },
        businessHours: formData.businessHours,
      });

      console.log('Update result:', result);
      setSaveMessage('Business details updated successfully!');
      setTimeout(() => setSaveMessage(''), 5000);
      await fetchBusiness(); // Refresh data
    } catch (error: any) {
      console.error('Error updating business:', error);
      console.error('Error response:', error.response);

      let errMsg = 'Failed to update business details';

      if (error.response?.data) {
        if (typeof error.response.data.error === 'string') {
          errMsg = error.response.data.error;
        } else if (error.response.data.message) {
          errMsg = error.response.data.message;
        }
      } else if (error.message) {
        errMsg = error.message;
      }

      setErrorMessage(errMsg);
      setTimeout(() => setErrorMessage(''), 5000);
    } finally {
      setSaving(false);
    }
  };

  const handleDayToggle = (day: string) => {
    setFormData({
      ...formData,
      businessHours: {
        ...formData.businessHours,
        [day]: {
          ...formData.businessHours[day as keyof typeof formData.businessHours],
          closed: !formData.businessHours[day as keyof typeof formData.businessHours].closed,
        },
      },
    });
  };

  const handleTimeChange = (day: string, field: 'open' | 'close', value: string) => {
    setFormData({
      ...formData,
      businessHours: {
        ...formData.businessHours,
        [day]: {
          ...formData.businessHours[day as keyof typeof formData.businessHours],
          [field]: value,
        },
      },
    });
  };

  if (loading) {
    return <LoadingAnimation message="Loading business details..." subtitle="Please wait" />;
  }

  if (!business) {
    return (
      <Box sx={{ maxWidth: 720, mx: 'auto' }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            Register Your Business
          </Typography>
          <Typography color="text.secondary">
            Submit your business for approval. Once an admin verifies it, it will be listed to customers.
          </Typography>
        </Box>

        {createError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {createError}
          </Alert>
        )}

        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <AddBusinessIcon sx={{ fontSize: 32, color: 'primary.main' }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Business Details
              </Typography>
            </Box>

            <Stack spacing={2.5}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  required
                  label="Business Name"
                  value={createForm.name}
                  onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                />
                <FormControl fullWidth required>
                  <InputLabel>Category</InputLabel>
                  <Select
                    label="Category"
                    value={createForm.category}
                    onChange={(e) => setCreateForm({ ...createForm, category: e.target.value })}
                  >
                    {BUSINESS_CATEGORIES.map((cat) => (
                      <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <TextField
                fullWidth
                required
                multiline
                rows={3}
                label="Description"
                value={createForm.description}
                onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
                helperText="Describe your business and what makes it special"
              />

              <Divider />

              <TextField
                fullWidth
                required
                label="Address"
                value={createForm.address}
                onChange={(e) => setCreateForm({ ...createForm, address: e.target.value })}
              />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  required
                  label="City"
                  value={createForm.city}
                  onChange={(e) => setCreateForm({ ...createForm, city: e.target.value })}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Latitude"
                  type="number"
                  value={createForm.latitude}
                  onChange={(e) => setCreateForm({ ...createForm, latitude: e.target.value })}
                  helperText="For map location (optional)"
                />
                <TextField
                  fullWidth
                  label="Longitude"
                  type="number"
                  value={createForm.longitude}
                  onChange={(e) => setCreateForm({ ...createForm, longitude: e.target.value })}
                  helperText="For map location (optional)"
                />
              </Box>

              <Divider />

              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  required
                  label="Phone Number"
                  value={createForm.phone}
                  onChange={(e) => setCreateForm({ ...createForm, phone: e.target.value })}
                />
                <TextField
                  fullWidth
                  required
                  label="Email"
                  type="email"
                  value={createForm.email}
                  onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                />
              </Box>

              <Button
                variant="contained"
                size="large"
                startIcon={<AddBusinessIcon />}
                onClick={handleCreate}
                disabled={creating}
                sx={{ alignSelf: 'flex-start' }}
              >
                {creating ? 'Submitting...' : 'Submit for Approval'}
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Box>
    );
  }

  const statusInfo = STATUS_INFO[business.status] || STATUS_INFO.pending;

  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          Business Management
        </Typography>
        <Typography color="text.secondary">
          Manage your business details and delivery information
        </Typography>
      </Box>

      <Alert severity={statusInfo.severity} sx={{ mb: 3 }}>
        {statusInfo.message}
      </Alert>

      {saveMessage && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {saveMessage}
        </Alert>
      )}

      {errorMessage && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {errorMessage}
        </Alert>
      )}

      {/* Business Info Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <BusinessIcon sx={{ fontSize: 32, color: 'primary.main' }} />
            <Box sx={{ flexGrow: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {business.name}
                </Typography>
                <Chip label={statusInfo.label} color={statusInfo.color} size="small" />
              </Box>
              <Typography variant="body2" color="text.secondary">
                Category: {business.category}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Stack spacing={3}>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Business Description
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                helperText="Describe your business and what makes it special"
              />
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Location Details
              </Typography>
              <Stack spacing={2}>
                <TextField
                  fullWidth
                  label="Address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  helperText="Full street address for customers to find you"
                />
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    fullWidth
                    label="City"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  />
                  <TextField
                    fullWidth
                    label="Delivery Area (City/Zone)"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    helperText="Where you can deliver orders"
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    fullWidth
                    label="Latitude"
                    type="number"
                    value={formData.latitude}
                    onChange={(e) => setFormData({ ...formData, latitude: e.target.value })}
                    helperText="For map location"
                  />
                  <TextField
                    fullWidth
                    label="Longitude"
                    type="number"
                    value={formData.longitude}
                    onChange={(e) => setFormData({ ...formData, longitude: e.target.value })}
                    helperText="For map location"
                  />
                </Box>
              </Stack>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Contact Information
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </Box>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Business Hours
              </Typography>
              <Stack spacing={2}>
                {days.map((day) => (
                  <Box key={day} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography sx={{ minWidth: 100, textTransform: 'capitalize' }}>
                      {day}
                    </Typography>
                    <FormControl size="small" sx={{ minWidth: 80 }}>
                      <Select
                        value={formData.businessHours[day as keyof typeof formData.businessHours].closed ? 'closed' : 'open'}
                        onChange={(e) => {
                          if (e.target.value === 'closed') {
                            handleDayToggle(day);
                          } else if (formData.businessHours[day as keyof typeof formData.businessHours].closed) {
                            handleDayToggle(day);
                          }
                        }}
                      >
                        <MenuItem value="open">Open</MenuItem>
                        <MenuItem value="closed">Closed</MenuItem>
                      </Select>
                    </FormControl>
                    {!formData.businessHours[day as keyof typeof formData.businessHours].closed && (
                      <>
                        <TextField
                          size="small"
                          type="time"
                          value={formData.businessHours[day as keyof typeof formData.businessHours].open}
                          onChange={(e) => handleTimeChange(day, 'open', e.target.value)}
                          sx={{ width: 150 }}
                        />
                        <Typography>to</Typography>
                        <TextField
                          size="small"
                          type="time"
                          value={formData.businessHours[day as keyof typeof formData.businessHours].close}
                          onChange={(e) => handleTimeChange(day, 'close', e.target.value)}
                          sx={{ width: 150 }}
                        />
                      </>
                    )}
                  </Box>
                ))}
              </Stack>
            </Box>

            <Button
              variant="contained"
              size="large"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={saving}
              sx={{ alignSelf: 'flex-start' }}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
